import React, { useEffect, useState } from 'react';
import {
  VStack,
  Box,
  Text,
  Button,
  Container,
  useColorModeValue,
  Heading,
  Icon,
  Flex,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiArrowRight, FiCreditCard, FiClock } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import useHosteaToast from '../../../ui/feedback/useHosteaToast';

const MotionBox = motion.create(Box);

const PaymentConfirmation = ({ onContinue, paymentType, amount, themeColor: propThemeColor }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useHosteaToast();
  const { t } = useTranslation();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('pending');

  // Hooks pour les couleurs
  const textColor = useColorModeValue('gray.600', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const boxBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const themeColor = propThemeColor || '#1f97ad'; // Utiliser la couleur passée en props ou la couleur par défaut
  console.log('TEST', themeColor);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/check-payment-status/${sessionId}`);
          const data = await response.json();
          
          
          if (data.status === 'paid' || data.status === 'processing') {
            // Utiliser dbPayment.amount en priorité si disponible, sinon data.amount
            const finalAmount = data.dbPayment?.amount || data.amount || 0;
            console.log('💰 [PaymentConfirmation] Amount calculation:', {
              'data.amount': data.amount,
              'data.dbPayment?.amount': data.dbPayment?.amount,
              'finalAmount': finalAmount
            });
            
            setPaymentDetails({
              amount: finalAmount,
              paymentType: data.paymentType,
              metadata: data.metadata,
              stripeStatus: data.stripeStatus,
              dbPayment: data.dbPayment
            });
            
            // Déterminer le statut d'approbation
            if (data.status === 'processing') {
              setPaymentStatus('pending');
            } else if (data.paymentType === 'arrival' && 
                data.metadata && 
                (data.metadata.service === 'early' || data.metadata.service === 'meet')) {
              setPaymentStatus('pending');
            } else {
              setPaymentStatus('approved');
            }
          } else {
            toast({
              title: t('stripe.paymentCancel.title'),
              description: t('stripe.paymentCancel.description'),
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            navigate('/checkin');
          }
        } catch (error) {
          toast({
            title: t('stripe.paymentCancel.title'),
            description: t('stripe.paymentCancel.description'),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate('/checkin');
        }
      }
      setIsLoading(false);
    };

    checkPaymentStatus();
  }, [searchParams, navigate, toast, t]);

  // Formater le montant (utiliser la prop amount en priorité, puis paymentDetails)
  const amountToDisplay = amount || paymentDetails?.amount || paymentDetails?.dbPayment?.amount || 0;
  const formattedAmount = (amountToDisplay / 100).toFixed(2);
  console.log('💰 [PaymentConfirmation] Formatted amount:', {
    'prop amount': amount,
    'paymentDetails?.amount': paymentDetails?.amount,
    'paymentDetails?.dbPayment?.amount': paymentDetails?.dbPayment?.amount,
    'amountToDisplay': amountToDisplay,
    'formattedAmount': formattedAmount
  });

  const getPaymentInfo = () => {
    const metadata = paymentDetails?.metadata || {};
    const isArrivalService = paymentDetails?.paymentType === 'arrival';
    const isEarlyOrMeet = metadata.service === 'early' || metadata.service === 'meet';
    
    // Forcer l'affichage du message d'attente pour early check-in et meet & greet
    if (isArrivalService && isEarlyOrMeet) {
      let serviceName = metadata.service === 'early' ? t('checkin.stepSix.earlyCheckIn') : t('checkin.stepSix.meetAndGreet');
      let serviceDescription = metadata.service === 'early' 
        ? t('checkin.stepSix.earlyCheckInDesc')
        : t('checkin.stepSix.meetAndGreetDesc');
      let approvalMessage = metadata.service === 'early'
        ? t('checkin.stepSix.earlyCheckInApproval')
        : t('checkin.stepSix.meetAndGreetApproval');
      
      return {
        title: t('checkin.stepSix.pendingServiceTitle', { service: serviceName }),
        description: t('checkin.stepSix.pendingPaymentDesc'),
        details: `${approvalMessage} ${t('checkin.stepSix.pendingPaymentDetails', { time: metadata.arrivalTime })}`,
        nextStep: t('checkin.stepSix.continue')
      };
    }
    
    // Pour les autres types de paiement
    switch (paymentDetails?.paymentType) {
      case 'taxi':
        return {
          title: t('checkin.stepTaxiBooking.confirmationTitle'),
          description: t('checkin.stepTaxiBooking.confirmationDesc'),
          details: t('checkin.stepTaxiBooking.confirmationDetails', { 
            airport: metadata.fromAirport,
            date: metadata.date,
            time: metadata.arrivalTime 
          }),
          nextStep: t('checkin.stepTaxiBooking.continue')
        };
      case 'arrival':
        if (metadata.service === 'luggage') {
          const luggageDetails = metadata.luggageDetails ? JSON.parse(metadata.luggageDetails) : null;
          return {
            title: t('checkin.stepSix.luggageServiceTitle'),
            description: t('checkin.stepSix.luggageServiceDesc'),
            details: t('checkin.stepSix.luggageServiceDetails', {
              time: metadata.arrivalTime,
              suitcases: luggageDetails?.suitcases || 0,
              bags: luggageDetails?.bags || 0
            }),
            nextStep: t('checkin.stepSix.continue')
          };
        }
        break;
      case 'deposit':
        return {
          title: t('checkin.stepSix.depositTitle'),
          description: t('checkin.stepSix.depositDesc'),
          details: t('checkin.stepSix.depositDetails'),
          nextStep: t('checkin.stepSix.continue')
        };
      case 'babybed':
        return {
          title: t('checkin.stepSix.babybedTitle'),
          description: t('checkin.stepSix.babybedDesc'),
          details: t('checkin.stepSix.babybedDetails'),
          nextStep: t('checkin.stepSix.continue')
        };
      case 'cleaning':
        const cleaningDays = metadata.cleaningDays ? JSON.parse(metadata.cleaningDays) : [];
        const daysCount = cleaningDays.length;
        return {
          title: t('checkin.stepSix.cleaningTitle'),
          description: t('checkin.stepSix.cleaningDesc', { count: daysCount }),
          details: t('checkin.stepSix.cleaningDetails', { 
            days: cleaningDays.join(', '),
            count: daysCount 
          }),
          nextStep: t('checkin.stepSix.continue')
        };
      default:
        return {
          title: t('stripe.paymentSuccess.title'),
          description: t('stripe.paymentSuccess.description'),
          details: t('checkin.stepSix.defaultDetails'),
          nextStep: t('checkin.stepSix.continue')
        };
    }
  };

  const handleContinue = () => {
    // Si onContinue est fourni en props, l'utiliser
    if (onContinue) {
      onContinue();
      return;
    }
    
    // Sinon, utiliser la logique par défaut
    const token = searchParams.get('token');
    if (token) {
      // Rediriger vers l'URL de base du check-in - CheckInForm gérera l'avancement automatiquement
      navigate(`/checkin/${token}`);
    } else {
      navigate('/checkin');
    }
  };

  if (isLoading) {
    return (
      <Container maxW="container.sm" py={8}>
        <Box textAlign="center">
          <Text>{t('common.loading')}</Text>
        </Box>
      </Container>
    );
  }

  const paymentInfo = getPaymentInfo();
  const isPending = paymentStatus === 'pending';

  return (
    <Container maxW="container.sm" py={8}>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <VStack spacing={8} align="stretch">
          {/* En-tête avec sélecteur de langue */}
          <Box position="relative" textAlign="center">
            <Box position="absolute" top={0} right={0}>
              <LanguageSwitcher />
            </Box>
            <Badge 
              colorScheme={isPending ? "yellow" : "green"} 
              fontSize="sm" 
              mb={4} 
              px={3} 
              py={1} 
              borderRadius="full"
              display="inline-block"
            >
              {isPending ? t('checkin.stepSix.pendingApproval') : t('stripe.paymentSuccess.title')}
            </Badge>
            <Heading
              fontSize="2xl"
              fontWeight="bold"
              mb={4}
              color={themeColor}
            >
              {paymentInfo.title}
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="500px" mx="auto" lineHeight="tall">
              {paymentInfo.description}
            </Text>
          </Box>

          {/* Carte de confirmation */}
          <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={8}
            bg={boxBg}
            shadow="lg"
          >
            <VStack spacing={6} align="center">
              <Icon 
                as={isPending ? FiClock : FiCheckCircle} 
                boxSize={16} 
                color={isPending ? "yellow.500" : "green.500"} 
              />
              
              <VStack spacing={2} align="center">
                <Text fontSize="xl" fontWeight="bold">
                  {isPending ? t('checkin.stepSix.pendingProcessing') : t('checkin.stepSix.thankYou')}
                </Text>
                <Text fontSize="md" color={textColor}>
                  {t('checkin.stepSix.amount')}: {formattedAmount}€
                </Text>
              </VStack>
              
              <Divider />
              
              <VStack spacing={4} align="center" width="100%">
                <Flex align="center" width="100%" justify="center">
                  <Icon as={FiCreditCard} mr={2} color={themeColor} />
                  <Text fontWeight="medium">{t('checkin.stepSix.secureTransaction')}</Text>
                </Flex>
                <Text fontSize="sm" color={textColor} textAlign="center">
                  {paymentInfo.details}
                </Text>
                
                {isPending && (paymentDetails?.metadata?.service === 'early' || paymentDetails?.metadata?.service === 'meet') && (
                  <Alert status="info" mt={4} borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>{t('checkin.stepSix.confirmationDelay')}</AlertTitle>
                      <AlertDescription>
                        {t('checkin.stepSix.confirmationDelayDesc')}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </VStack>
          </Box>

          {/* Bouton de continuation */}
          <Button
            rightIcon={<FiArrowRight />}
            onClick={handleContinue}
            bg={themeColor}
            color="white"
            size="lg"
            height="60px"
            fontSize="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            transition="all 0.2s"
          >
            {paymentInfo.nextStep}
          </Button>
        </VStack>
      </MotionBox>
    </Container>
  );
};

export default PaymentConfirmation; 